import { Box, Stack, Typography } from "@mui/material";
import { GridEventListener } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useUserGridColumns } from "components/users/userGridColumns";
import { UserRow } from "components/users/userAdminFilters";
import { AccountType } from "store/api/generatedApi";

export const clickableRowsSx = {
  flex: 1,
  "& .MuiDataGrid-row": { cursor: "pointer" },
} as const;

export const rowClickListener =
  (onRowClick: (id: string) => void): GridEventListener<"cellClick"> =>
  (params) => {
    if (params.field !== "__check__" && params.field !== "actions") {
      onRowClick(String(params.id));
    }
  };

export const UsersDataGrid = ({
  rows,
  loading,
  accountType,
  selection,
  onSelectionChange,
  onRowClick,
  onCreateInviteLink,
  onSendInviteEmail,
  emptyText,
}: {
  rows: UserRow[];
  loading: boolean;
  accountType: AccountType;
  selection: string[];
  onSelectionChange: (ids: string[]) => void;
  onRowClick: (uid: string) => void;
  onCreateInviteLink: (email: string) => void;
  onSendInviteEmail: (email: string) => void;
  emptyText: string;
}) => {
  const columns = useUserGridColumns({
    accountType,
    onCreateInviteLink,
    onSendInviteEmail,
  });

  return (
    <Stack sx={{ flex: 1, minHeight: 400 }}>
      <Box sx={{ flex: 1, width: "100%" }}>
        <HotkeyCompliantDataGrid<UserRow>
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.uid}
          checkboxSelection
          pagination
          hideFooter={false}
          pageSizeOptions={[10, 25, 50, 100]}
          rowSelectionModel={{ type: "include", ids: new Set(selection) }}
          onRowSelectionModelChange={(model) => {
            onSelectionChange([...model.ids].map(String));
          }}
          onCellClick={rowClickListener(onRowClick)}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "name", sort: "asc" }] },
          }}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack
                sx={{ height: "100%" }}
                alignItems="center"
                justifyContent="center"
              >
                <Typography color="text.secondary">{emptyText}</Typography>
              </Stack>
            ),
          }}
          sx={clickableRowsSx}
        />
      </Box>
    </Stack>
  );
};
